import { supabase } from '../lib/supabase';

export const consentService = {
  // Get treatment plan with all related data
  async getTreatmentPlanForReview(treatmentPlanId) {
    const { data, error } = await supabase?.from('treatment_plans')?.select(`
        *,
        patient:patient_id(id, email, full_name),
        provider:provider_id(id, email, full_name),
        phases:treatment_plan_phases(
          *,
          procedures:treatment_plan_procedures(*)
        ),
        alternatives:treatment_plan_alternatives(*),
        consent_workflow:consent_workflows(
          *,
          acknowledgments:consent_acknowledgments(*),
          discussions:consent_discussions(
            *,
            sender:sender_id(id, full_name)
          ),
          signature:digital_signatures(*)
        )
      `)?.eq('id', treatmentPlanId)?.single();
    
    if (error) throw error;
    
    // Convert to camelCase
    return {
      id: data?.id,
      patientId: data?.patient_id,
      providerId: data?.provider_id,
      planName: data?.plan_name,
      description: data?.description,
      estimatedDuration: data?.estimated_duration,
      totalCost: data?.total_cost,
      insuranceCoverage: data?.insurance_coverage,
      patientResponsibility: data?.patient_responsibility,
      status: data?.status,
      patient: data?.patient,
      provider: data?.provider,
      phases: data?.phases?.map(phase => ({
        id: phase?.id,
        treatmentPlanId: phase?.treatment_plan_id,
        phaseName: phase?.phase_name,
        phaseOrder: phase?.phase_order,
        description: phase?.description,
        estimatedStartDate: phase?.estimated_start_date,
        estimatedEndDate: phase?.estimated_end_date,
        cost: phase?.cost,
        status: phase?.status,
        procedures: phase?.procedures?.map(proc => ({
          id: proc?.id,
          phaseId: proc?.phase_id,
          procedureCode: proc?.procedure_code,
          procedureName: proc?.procedure_name,
          toothNumber: proc?.tooth_number,
          cost: proc?.cost,
          status: proc?.status,
          scheduledDate: proc?.scheduled_date,
          notes: proc?.notes
        }))
      })),
      alternatives: data?.alternatives?.map(alt => ({
        id: alt?.id,
        treatmentPlanId: alt?.treatment_plan_id,
        alternativeName: alt?.alternative_name,
        description: alt?.description,
        estimatedCost: alt?.estimated_cost,
        estimatedDuration: alt?.estimated_duration,
        pros: alt?.pros,
        cons: alt?.cons,
        outcomeProjection: alt?.outcome_projection
      })),
      consentWorkflow: data?.consent_workflow?.[0] ? {
        id: data?.consent_workflow?.[0]?.id,
        treatmentPlanId: data?.consent_workflow?.[0]?.treatment_plan_id,
        patientId: data?.consent_workflow?.[0]?.patient_id,
        providerId: data?.consent_workflow?.[0]?.provider_id,
        consentStatus: data?.consent_workflow?.[0]?.consent_status,
        reviewStartedAt: data?.consent_workflow?.[0]?.review_started_at,
        reviewCompletedAt: data?.consent_workflow?.[0]?.review_completed_at,
        patientQuestions: data?.consent_workflow?.[0]?.patient_questions,
        providerResponse: data?.consent_workflow?.[0]?.provider_response,
        requiresConsultation: data?.consent_workflow?.[0]?.requires_consultation,
        consultationScheduledAt: data?.consent_workflow?.[0]?.consultation_scheduled_at,
        acknowledgments: data?.consent_workflow?.[0]?.acknowledgments?.map(ack => ({
          id: ack?.id,
          consentWorkflowId: ack?.consent_workflow_id,
          phaseId: ack?.phase_id,
          acknowledgmentText: ack?.acknowledgment_text,
          acknowledged: ack?.acknowledged,
          acknowledgedAt: ack?.acknowledged_at
        })),
        discussions: data?.consent_workflow?.[0]?.discussions?.map(disc => ({
          id: disc?.id,
          consentWorkflowId: disc?.consent_workflow_id,
          senderId: disc?.sender_id,
          messageText: disc?.message_text,
          isProvider: disc?.is_provider,
          readAt: disc?.read_at,
          createdAt: disc?.created_at,
          sender: disc?.sender
        })),
        signature: data?.consent_workflow?.[0]?.signature?.[0]
      } : null,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Get all treatment plans for current patient
  async getPatientTreatmentPlans() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase?.from('treatment_plans')?.select(`
        *,
        provider:provider_id(full_name),
        consent_workflow:consent_workflows(consent_status)
      `)?.eq('patient_id', user?.id)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(plan => ({
      id: plan?.id,
      planName: plan?.plan_name,
      description: plan?.description,
      estimatedDuration: plan?.estimated_duration,
      totalCost: plan?.total_cost,
      status: plan?.status,
      providerName: plan?.provider?.full_name,
      consentStatus: plan?.consent_workflow?.[0]?.consent_status,
      createdAt: plan?.created_at
    }));
  },

  // Update consent workflow status
  async updateConsentStatus(consentWorkflowId, status, additionalData = {}) {
    const updates = {
      consent_status: status,
      updated_at: new Date()?.toISOString(),
      ...additionalData
    };
    
    const { data, error } = await supabase?.from('consent_workflows')?.update(updates)?.eq('id', consentWorkflowId)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Update acknowledgment
  async updateAcknowledgment(acknowledgmentId, acknowledged) {
    const { data, error } = await supabase?.from('consent_acknowledgments')?.update({
        acknowledged,
        acknowledged_at: acknowledged ? new Date()?.toISOString() : null
      })?.eq('id', acknowledgmentId)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Save digital signature
  async saveDigitalSignature(consentWorkflowId, signatureData, method = 'digital_signature') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase?.from('digital_signatures')?.insert({
        consent_workflow_id: consentWorkflowId,
        patient_id: user?.id,
        signature_data: signatureData,
        signature_method: method,
        timestamp: new Date()?.toISOString(),
        identity_verified: true,
        is_valid: true
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Add discussion message
  async addDiscussionMessage(consentWorkflowId, messageText, isProvider = false) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase?.from('consent_discussions')?.insert({
        consent_workflow_id: consentWorkflowId,
        sender_id: user?.id,
        message_text: messageText,
        is_provider: isProvider
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Send email notification via Edge Function
  async sendConsentNotification(notificationData) {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    
    const response = await fetch(`${supabaseUrl}/functions/v1/send-consent-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify(notificationData)
    });
    
    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to send notification');
    }
    
    const result = await response?.json();
    
    // Log notification in database
    await supabase?.from('consent_email_notifications')?.insert({
        consent_workflow_id: notificationData?.consentWorkflowId,
        recipient_email: notificationData?.recipientEmail,
        notification_type: notificationData?.notificationType,
        notification_status: 'sent',
        subject: notificationData?.subject || 'Treatment Plan Notification',
        sent_at: new Date()?.toISOString(),
        resend_message_id: result?.messageId
      });
    
    return result;
  },

  // Request consultation
  async requestConsultation(consentWorkflowId, questions) {
    const { data, error } = await supabase?.from('consent_workflows')?.update({
        requires_consultation: true,
        patient_questions: questions,
        consent_status: 'requires_discussion',
        updated_at: new Date()?.toISOString()
      })?.eq('id', consentWorkflowId)?.select()?.single();
    
    if (error) throw error;
    return data;
  }
};